import { GA_MEASUREMENT_ID } from './tagConfig';

// Type definitions for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export { GA_MEASUREMENT_ID };

type EventParams = Record<string, string | number | boolean>;

const currentPath = (): string =>
  typeof window === 'undefined' ? '' : window.location.pathname;

/**
 * Pushes a named conversion event to both gtag and the GTM dataLayer so
 * marketing can build triggers on either surface.
 */
const trackNamedEvent = (eventName: string, params: EventParams) => {
  const payload: EventParams = { page_path: currentPath(), ...params };
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, payload);
  }
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
  }
};

export const trackContactSubmit = (pageSource: string, companyStage: string) =>
  trackNamedEvent('contact_submit', {
    form_name: 'contact_form',
    page_source: pageSource,
    company_stage: companyStage,
  });

export const trackScoreComplete = (track: string, score: number, band: string) =>
  trackNamedEvent('score_complete', { track, score, band });

export const trackScoreLeadSubmit = (band: string, score: number) =>
  trackNamedEvent('score_lead_submit', { band, score });

export const trackBlueprintSubmit = (
  industry: string,
  timeline: string,
  aiMaturity: string,
) =>
  trackNamedEvent('blueprint_submit', {
    industry,
    timeline,
    ai_maturity: aiMaturity,
  });

export const trackEmailClick = (location: string) =>
  trackNamedEvent('email_click', { location });

/**
 * Track page views for SPA navigation
 */
export const trackPageView = (path: string, title?: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  }
};

/**
 * Track custom events
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, string | number | boolean>
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, params);
  }
};

/**
 * Track CTA button clicks
 * @param ctaName - Name of the CTA (e.g., "Start a Strategic Conversation")
 * @param location - Location on page (e.g., "hero", "navbar", "footer")
 */
export const trackCTAClick = (ctaName: string, location: string) => {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_location: location,
  });
};

/**
 * Track form submission outcomes
 * @param formName - Name of the form (e.g., "contact_form", "founder_form")
 * @param success - Whether submission was successful
 */
export const trackFormSubmission = (formName: string, success: boolean) => {
  trackEvent('form_submission', {
    form_name: formName,
    success: success,
  });
};

/**
 * Track path/tile selections
 * @param pathName - Name of the selected path
 * @param location - Section where path was selected
 */
/**
 * Track conversion events using GA4's recommended `generate_lead` event
 * @param conversionType - Type of conversion (e.g., "schedule_a_call", "founder_inquiry")
 * @param value - Optional monetary value for the conversion
 */
export const trackConversion = (conversionType: string, value?: number) => {
  trackEvent('generate_lead', {
    event_category: 'conversion',
    conversion_type: conversionType,
    value: value || 0,
    currency: 'USD',
  });
};

export const trackPathSelection = (pathName: string, location: string) => {
  trackEvent('path_selection', {
    path_name: pathName,
    selection_location: location,
  });
};

export const trackQuizStart = (quizName: string) => {
  trackEvent('quiz_start', { quiz_name: quizName });
};

export const trackQuizComplete = (quizName: string, scoreBand: string, score: number) => {
  trackEvent('quiz_complete', {
    quiz_name: quizName,
    score_band: scoreBand,
    score,
  });
};

export const trackLeadSubmit = (formName: string) => {
  trackEvent('lead_submit', { form_name: formName });
};

export const trackBookingClick = (location: string) => {
  trackEvent('booking_click', { cta_location: location });
};

export const trackHeroScenarioSwitch = (label: string) =>
  trackNamedEvent('hero_scenario_switch', { label });

export const trackProblemTabSelect = (label: string) =>
  trackNamedEvent('problem_tab_select', { label });

export const trackBentoTileHover = (label: string) =>
  trackNamedEvent('bento_tile_hover', { label });

export const trackResiliencePreviewCompleted = (band: string) =>
  trackNamedEvent('resilience_preview_completed', { band });

export const trackCareersApplyClick = (role: string) =>
  trackNamedEvent('careers_apply_click', { label: role });
